import {AppContext, AppContextI} from "../../../components/context/ContextWrapper";
import {useContext, useEffect, useState} from "react";
import {BeaconTrigger, GpsShapeType, GpsTrigger, LatLong, Trigger, WiFiApTrigger, Zone} from "../../../components/types";
import {Button, Flex, Grid} from "@chakra-ui/react";
import {v4 as uuidv4} from "uuid";
import BeaconTriggerDetails from "../../../components/spotList/details/BeaconTriggerDetails";
import WiFiApTriggerDetails from "../../../components/spotList/details/WiFiApTriggerDetails";
import GpsTriggerDetails from "../../../components/spotList/details/GpsTriggerDetails";
import DraftBeaconTriggerDetails from "../../../components/spotList/details/DraftBeaconTriggerDetails";
import {MapContext, MapContextI} from "../../../components/context/MapContextWrapper";

type Props = {
    zone: Zone
    allowGps?: boolean
}

const ZoneTriggers = ({zone, allowGps = true}: Props) => {
    const appContext: AppContextI = useContext<AppContextI>(AppContext);
    const mapContext: MapContextI = useContext<MapContextI>(MapContext);
    const bleEnabled = appContext.isBleEnabled();
    const [pendingBeaconIds, setPendingBeaconIds] = useState<string[]>([]);

    useEffect(() => {
        setPendingBeaconIds([]);
    }, [zone.id]);

    return <>
        <Grid templateColumns={"min-content 1fr min-content"} marginTop={"8px"}>

            <Flex columnGap={"4px"}>
                {bleEnabled ? (
                    <Button size={"sm"}
                            onClick={() => {
                                setPendingBeaconIds((prev) => [...prev, uuidv4()]);
                            }
                            }
                            disabled={!appContext.isDraft()}>
                        Add Beacon
                    </Button>
                ) : null}

                <Button size={"sm"}
                        onClick={() => {
                            const newTriggers: Trigger[] = [...zone.wiFiTriggers ? zone.wiFiTriggers : [],
                                {
                                    type: "WiFiAP",
                                    id: uuidv4(),
                                    name: ""
                                } as WiFiApTrigger
                            ];
                            appContext.updateZone(zone, {
                                ...zone,
                                wiFiTriggers: newTriggers
                            } as Zone)
                        }
                        }
                        disabled={!appContext.isDraft()}>
                    Add WiFi AP
                </Button>

                <Button size={"sm"}
                        disabled={!allowGps || !appContext.isDraft() || (zone.gpsTriggers != null && zone.gpsTriggers.length > 0)}
                        onClick={() => {
                            const center = mapContext.getMapCenter() || {lat: 0, lng: 0};
                            const latSpan = mapContext.getMapLatSpan() || 0.001;
                            // For this triangle template, vertical height is 1.5 * offset.
                            // Target initial polygon height at about 20% of visible map height.
                            const offset = latSpan * (0.2 / 1.5);
                            const defaultCoords: LatLong[] = [
                                {lat: center.lat + offset, lng: center.lng},
                                {lat: center.lat - offset / 2, lng: center.lng - offset},
                                {lat: center.lat - offset / 2, lng: center.lng + offset}
                            ];
                            const newTrigger: GpsTrigger = {
                                id: uuidv4(),
                                type: "GPS",
                                shapeType: GpsShapeType.Polygon,
                                polygonCoords: defaultCoords,
                                zoneId: zone.id
                            };
                            appContext.updateZone(zone, {
                                ...zone,
                                gpsTriggers: [...(zone.gpsTriggers || []), newTrigger]
                            } as Zone);
                        }
                        }
                >
                    Add GPS area
                </Button>
            </Flex>
        </Grid>
        <Grid gridTemplateColumns={"max-content 1fr 24px max-content"} rowGap={"8px"} columnGap={2} marginTop={"8px"}>
            {bleEnabled ? (
                <>
                    {
                        pendingBeaconIds.map((pendingId) => (
                            <DraftBeaconTriggerDetails
                                key={`beacon-draft-${pendingId}`}
                                onCreate={(payload) => {
                                    const draftTrigger: BeaconTrigger = payload.type === "Beacon"
                                        ? {
                                            type: "Beacon",
                                            macAddr: payload.macAddr,
                                            majorId: "",
                                            minorId: "",
                                            id: pendingId
                                        } as BeaconTrigger
                                        : {
                                            type: "iBeacon",
                                            macAddr: "",
                                            uuid: payload.uuid,
                                            majorId: payload.majorId,
                                            minorId: payload.minorId,
                                            id: pendingId
                                        } as BeaconTrigger;
                                    const newTriggers: Trigger[] = [draftTrigger, ...(zone.beaconTriggers || [])];
                                    appContext.updateZone(zone, {
                                        ...zone,
                                        beaconTriggers: newTriggers
                                    } as Zone);
                                    setPendingBeaconIds((prev) => prev.filter((id) => id !== pendingId));
                                }}
                                onCancel={() => {
                                    setPendingBeaconIds((prev) => prev.filter((id) => id !== pendingId));
                                }}
                                disabled={!appContext.isDraft()}
                            />
                        ))
                    }
                    {
                        zone.beaconTriggers?.map((beaconTrigger: BeaconTrigger, index: number) =>
                            <BeaconTriggerDetails updateCall={appContext.updateZone} key={"beacon_" + beaconTrigger.id}
                                                  trigger={beaconTrigger}
                                                  object={zone} index={index}/>
                        )
                    }
                </>
            ) : null}
            {
                zone.wiFiTriggers?.map((wiFiApTrigger: WiFiApTrigger, index: number) =>
                    <WiFiApTriggerDetails updateCall={appContext.updateZone}
                                          key={"wifiAp_" + wiFiApTrigger.id}
                                          trigger={wiFiApTrigger}
                                          index={index}
                                          object={zone}
                    />
                )
            }
            {
                zone.gpsTriggers?.map((gpsTrigger: GpsTrigger, index: number) =>
                    <GpsTriggerDetails updateCall={appContext.updateZone}
                                       key={"gps_" + gpsTrigger.id}
                                       trigger={gpsTrigger}
                                       object={zone} index={index}
                    />
                )
            }
        </Grid>
    </>
}

export default ZoneTriggers
